/**
 * localStorage utilities for persisting drag-and-drop ordering
 */

// Keys for localStorage
const STORAGE_KEYS = {
  TEST_ORDER: 'interviewzen_test_order',
  QUESTION_ORDER: 'interviewzen_question_order'
} as const;

/**
 * Generic localStorage utility functions
 */
const storage = {
  get: <T>(key: string): T | null => {
    try {
      const item = localStorage.getItem(key);
      return item ? JSON.parse(item) : null;
    } catch (error) {
      console.error(`Error reading from localStorage (${key}):`, error);
      return null;
    }
  },

  set: <T>(key: string, value: T): void => {
    try {
      localStorage.setItem(key, JSON.stringify(value));
    } catch (error) {
      console.error(`Error writing to localStorage (${key}):`, error);
    }
  },

  remove: (key: string): void => {
    try {
      localStorage.removeItem(key);
    } catch (error) {
      console.error(`Error removing from localStorage (${key}):`, error);
    }
  }
};

/**
 * Test ordering utilities
 */
export const testOrdering = {
  /**
   * Get saved test order array
   */
  getOrder: (): string[] => {
    return storage.get<string[]>(STORAGE_KEYS.TEST_ORDER) || [];
  },

  /**
   * Save test order array
   */
  setOrder: (testIds: string[]): void => {
    storage.set(STORAGE_KEYS.TEST_ORDER, testIds);
  },

  /**
   * Add a new test to the saved order (at the beginning)
   */
  addTest: (testId: string): void => {
    const currentOrder = testOrdering.getOrder();
    const newOrder = [testId, ...currentOrder.filter(id => id !== testId)];
    testOrdering.setOrder(newOrder);
  },

  /**
   * Remove a test from the saved order
   */
  removeTest: (testId: string): void => {
    const currentOrder = testOrdering.getOrder();
    const newOrder = currentOrder.filter(id => id !== testId);
    testOrdering.setOrder(newOrder);
  },

  /**
   * Reorder tests based on drag-and-drop result
   */
  reorderTests: (sourceIndex: number, destinationIndex: number): void => {
    const currentOrder = testOrdering.getOrder();
    const newOrder = [...currentOrder];
    const [removed] = newOrder.splice(sourceIndex, 1);
    newOrder.splice(destinationIndex, 0, removed);
    testOrdering.setOrder(newOrder);
  },

  /**
   * Apply saved order to a list of tests
   */
  applyOrder: <T extends { uuid: string }>(tests: T[]): T[] => {
    const savedOrder = testOrdering.getOrder();
    
    if (savedOrder.length === 0) {
      return tests;
    }

    // Create a map for quick lookup
    const testsMap = tests.reduce((map, test) => {
      map[test.uuid] = test;
      return map;
    }, {} as Record<string, T>);

    // Return tests in saved order, filtering out any that don't exist
    const orderedTests = savedOrder
      .map(id => testsMap[id])
      .filter(Boolean);

    // Add any new tests that aren't in our saved order
    const orderedIds = new Set(savedOrder);
    const newTests = tests.filter(test => !orderedIds.has(test.uuid));

    return [...orderedTests, ...newTests];
  }
};

/**
 * Question ordering utilities
 */
export const questionOrdering = {
  /**
   * Get saved question order for a specific test
   */
  getOrder: (testId: string): string[] => {
    const allOrders = storage.get<Record<string, string[]>>(STORAGE_KEYS.QUESTION_ORDER) || {};
    return allOrders[testId] || [];
  },

  /**
   * Save question order for a specific test
   */
  setOrder: (testId: string, questionIds: string[]): void => {
    const allOrders = storage.get<Record<string, string[]>>(STORAGE_KEYS.QUESTION_ORDER) || {};
    allOrders[testId] = questionIds;
    storage.set(STORAGE_KEYS.QUESTION_ORDER, allOrders);
  },

  /**
   * Add a new question to the saved order for a test
   */
  addQuestion: (testId: string, questionId: string): void => {
    const currentOrder = questionOrdering.getOrder(testId);
    const newOrder = [...currentOrder.filter(id => id !== questionId), questionId];
    questionOrdering.setOrder(testId, newOrder);
  },

  /**
   * Remove a question from the saved order for a test
   */
  removeQuestion: (testId: string, questionId: string): void => {
    const currentOrder = questionOrdering.getOrder(testId);
    const newOrder = currentOrder.filter(id => id !== questionId);
    questionOrdering.setOrder(testId, newOrder);
  },

  /**
   * Reorder questions within a test based on drag-and-drop result
   */
  reorderQuestions: (testId: string, sourceIndex: number, destinationIndex: number): void => {
    const currentOrder = questionOrdering.getOrder(testId);
    const newOrder = [...currentOrder];
    const [removed] = newOrder.splice(sourceIndex, 1);
    newOrder.splice(destinationIndex, 0, removed);
    questionOrdering.setOrder(testId, newOrder);
  },

  /**
   * Apply saved order to a list of questions for a specific test
   */
  applyOrder: <T extends { id: string }>(testId: string, questions: T[]): T[] => {
    const savedOrder = questionOrdering.getOrder(testId);
    
    if (savedOrder.length === 0) {
      return questions;
    }

    // Create a map for quick lookup
    const questionsMap = questions.reduce((map, question) => {
      map[question.id] = question;
      return map;
    }, {} as Record<string, T>);

    // Return questions in saved order, filtering out any that don't exist
    const orderedQuestions = savedOrder
      .map(id => questionsMap[id])
      .filter(Boolean);

    // Add any new questions that aren't in our saved order
    const orderedIds = new Set(savedOrder);
    const newQuestions = questions.filter(question => !orderedIds.has(question.id));

    return [...orderedQuestions, ...newQuestions];
  },

  /**
   * Clear all question orders for a specific test
   */
  clearTestOrders: (testId: string): void => {
    const allOrders = storage.get<Record<string, string[]>>(STORAGE_KEYS.QUESTION_ORDER) || {};
    delete allOrders[testId];
    storage.set(STORAGE_KEYS.QUESTION_ORDER, allOrders);
  }
};